import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import { Category } from "../types";
import { apiClient } from "../utils/apiClient";

const CategoryMaintenance = () => {
  const API_PREFIX = "/api";

  // 配列として初期化 (型を指定)
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState("");
  // 成功メッセージ用のステートを追加
  const [successMessage, setSuccessMessage] = useState("");

  // 新規登録用ステート
  const [newName, setNewName] = useState("");
  const [newOrder, setNewOrder] = useState("");

  // 編集用ステート
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editOrder, setEditOrder] = useState<number | string>("");

  // データ取得
  const fetchCategories = () => {
    apiClient
      .get(`${API_PREFIX}/categories`)
      .then((data) => {
        console.log("Fetched categories:", data);
        if (Array.isArray(data)) {
          setCategories(data);
        } else {
          console.error("データが配列ではありません:", data);
          setCategories([]); // 配列でない場合は空にしておく
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setError("データの取得に失敗しました");
      });
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // メッセージリセット用ヘルパー
  const resetMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  // 新規追加
  const handleCreate = async () => {
    if (!newName) return;
    resetMessages(); // 処理開始時にメッセージをクリア

    try {
      await apiClient.post(`${API_PREFIX}/categories`, {
        category: { name: newName, display_order: newOrder },
      });
      setNewName("");
      setNewOrder("");
      setSuccessMessage("登録しました");
      fetchCategories();
    } catch (e: unknown) {
      console.error(e);
      // e が Errorオブジェクト（またはそのサブクラス）かどうかをチェック
      if (e instanceof Error) {
        setError(e.message);
      } else {
        // 文字列や謎のオブジェクトがthrowされた場合のフォールバック
        setError("予期せぬエラーが発生しました");
      }
    }
  };

  // 削除
  const handleDelete = async (id: number) => {
    if (!confirm("本当に削除しますか？")) return;
    resetMessages(); // 処理開始時にメッセージをクリア

    try {
      await apiClient.delete(`${API_PREFIX}/categories/${id}`);
      setSuccessMessage("削除しました");
      // エラーが起きなければ削除成功とみなして再取得
      fetchCategories();
    } catch (e: any) {
      // エラー時
      console.error(e);
      setError(e.message || "削除に失敗しました");
    }
  };

  // 編集モード開始
  const startEdit = (cat: Category) => {
    resetMessages();
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditOrder(cat.display_order);
  };

  // 編集キャンセル
  const cancelEdit = () => {
    resetMessages();
    setEditingId(null);
    setEditName("");
    setEditOrder("");
  };

  // 更新保存
  const handleUpdate = async (id: number) => {
    resetMessages(); // 処理開始時にメッセージをクリア
    try {
      await apiClient.put(`${API_PREFIX}/categories/${id}`, {
        category: { name: editName, display_order: editOrder },
      });
      setEditingId(null);
      setSuccessMessage("更新しました"); // 成功メッセージセット
      fetchCategories();
    } catch (e: any) {
      // エラー時
      console.error(e);
      setError(e.message || "更新に失敗しました");
    }
  };

  return (
    <div
      className="container-fluid p-0"
      style={{ minHeight: "100vh", backgroundColor: "#fff" }}
    >
      <Header title="カテゴリマスタ管理" />

      <div className="limit-width px-4">
        <div className="mb-4">
          <Link
            to="/admin"
            className="text-decoration-none text-secondary d-inline-flex align-items-center"
          >
            <span className="fs-4 me-2">‹</span>
            管理者メニューに戻る
          </Link>
        </div>

        {/* 成功メッセージエリア */}
        {successMessage && (
          <div
            className="alert alert-success d-flex align-items-center mb-4"
            role="alert"
          >
            <span className="me-2">✅</span>
            <div>{successMessage}</div>
          </div>
        )}

        {/* エラーメッセージエリア */}
        {error && (
          <div
            className="alert alert-danger d-flex align-items-center mb-4"
            role="alert"
          >
            <span className="me-2">⚠️</span>
            <div>{error}</div>
          </div>
        )}

        {/* 新規登録フォーム */}
        <div className="card mb-4 shadow-sm border-0 bg-light">
          <div className="card-body d-flex align-items-end gap-3">
            <div style={{ flex: 1 }}>
              <label className="form-label fw-bold small">カテゴリ名</label>
              <input
                type="text"
                className="form-control"
                placeholder="新規追加するカテゴリ名を入力"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div style={{ width: "100px" }}>
              <label className="form-label fw-bold small">表示順</label>
              <input
                type="number"
                className="form-control"
                placeholder="10"
                value={newOrder}
                onChange={(e) => setNewOrder(e.target.value)}
              />
            </div>
            <button className="btn btn-primary fw-bold" onClick={handleCreate}>
              追加
            </button>
          </div>
        </div>

        {/* 一覧テーブル */}
        <div className="table-responsive">
          <table className="table table-striped table-hover border align-middle">
            <thead className="bg-primary text-white">
              <tr>
                <th style={{ width: "15%" }}>表示順</th>
                <th style={{ width: "55%" }}>カテゴリ名</th>
                <th style={{ width: "30%" }} className="text-center">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {/* 配列チェックを行ってからmapする */}
              {Array.isArray(categories) &&
                categories.map((cat) => (
                  <tr key={cat.id}>
                    {editingId === cat.id ? (
                      <>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={editOrder || ""}
                            onChange={(e) => setEditOrder(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={editName || ""}
                            onChange={(e) => setEditName(e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-success btn-sm me-2"
                            onClick={() => handleUpdate(cat.id)}
                          >
                            保存
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={cancelEdit}
                          >
                            中止
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{cat.display_order}</td>
                        <td>{cat.name}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-outline-primary btn-sm me-2"
                            onClick={() => startEdit(cat)}
                          >
                            編集
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(cat.id)}
                          >
                            削除
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CategoryMaintenance;
